import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, MessageCircle, Star } from "@digital-twins/design-system/icons";
import {
  measureOf,
  SocialPlatform,
  socialTopicHighlights,
  SocialTopicHighlightItem,
  SocialTopicPostItem,
} from "@digital-twins/shared-types";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { SOCIAL_PLATFORM_TEXT_CLASS, socialPlatformColorStyle } from "./charts/socialChartColors";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialTopicPanelHeader } from "./SocialTopicPanelHeader";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { useTopicHighlight } from "./SocialTopicHighlightContext";
import { formatSocialCount, SOCIAL_PLATFORM_LABELS } from "./socialFormat";
import { useSocialUniversePosts } from "./useSocialUniversePosts";
import { useSocialUniverseCounts } from "./useSocialUniverse";

/**
 * Frozen for the Highlights card (plan D8): 3 numeric items with cite chips,
 * Filter and Why, then the Ask row.
 */
interface SocialTopicInsightsProps {
  context: SocialTopicSlotContext;
  /** False hides the Ask row (the back office). Defaults to false while `onAsk` is absent. */
  canAsk?: boolean;
  /** Opens the Assistant 2.0 entry scoped to the Topic with this question. */
  onAsk?: (question: string) => void;
}

const MAX_SUGGESTIONS = 3;

function platformLabel(key: unknown): string {
  return SOCIAL_PLATFORM_LABELS[key as SocialPlatform] ?? String(key ?? "");
}

function citeLabel(post: SocialTopicPostItem): string {
  const handle = post.authorHandle?.trim().replace(/^@+/, "");
  return handle ? `@${handle}` : platformLabel(post.platform);
}

/**
 * The Highlights card: at most 3 numeric items from `socialTopicHighlights`
 * (the same filters as the stat row), each citing the post behind it, with
 * Filter and Why, then the Ask row that opens the assistant. Items that do
 * not hold under the filters are not drawn, never n/a.
 */
export function SocialTopicInsights(props: SocialTopicInsightsProps) {
  const { context, canAsk, onAsk } = props;
  const { universe, topicFilters, refreshKey, brandNames, onOpenPost } = context;

  const { t, i18n } = useTranslation("app");
  const { setActive } = useTopicHighlight();
  const [question, setQuestion] = useState("");
  const token = universe.token;
  const hasBrands = universe.brands.length > 0;
  const { metric, sentiment } = topicFilters.filters;

  const allFilters = useMemo(() => topicFilters.facetFilters(), [topicFilters]);
  const channelFilters = useMemo(() => topicFilters.facetFilters("channel"), [topicFilters]);
  const brandFilters = useMemo(() => topicFilters.facetFilters("brand"), [topicFilters]);
  const negativeFilters = useMemo(
    () => ({ ...channelFilters, sentiment: "negative" as const }),
    [channelFilters],
  );
  // A positive or neutral sentiment filter has no negative posts to compare.
  const asksNegatives = sentiment == null || sentiment === "negative";

  const { counts: totals } = useSocialUniverseCounts(
    token,
    { groupBy: "channel", filters: allFilters, includeMarket: true },
    refreshKey,
  );
  const { counts: channelCounts } = useSocialUniverseCounts(
    token,
    { groupBy: "channel", filters: channelFilters, includeMarket: true },
    refreshKey,
  );
  const { counts: negativeChannelCounts } = useSocialUniverseCounts(
    asksNegatives ? token : null,
    { groupBy: "channel", filters: negativeFilters },
    refreshKey,
  );
  const { counts: brandCounts } = useSocialUniverseCounts(
    hasBrands ? token : null,
    { groupBy: "brand", filters: brandFilters },
    refreshKey,
  );
  const { posts: topPosts, isLoading: isLoadingTopPost } = useSocialUniversePosts(token, {
    filters: allFilters,
    sort: "views",
    limit: 1,
    refreshKey,
  });
  const topPost = topPosts[0] ?? null;

  const isReady =
    totals != null &&
    channelCounts != null &&
    (!asksNegatives || negativeChannelCounts != null) &&
    (!hasBrands || brandCounts != null) &&
    !isLoadingTopPost;

  const items = useMemo(
    () =>
      isReady
        ? socialTopicHighlights({
            metric,
            totals,
            topPost,
            brandCounts,
            brandNames,
            channelCounts,
            negativeChannelCounts: asksNegatives ? negativeChannelCounts : null,
          })
        : [],
    [
      isReady,
      metric,
      totals,
      topPost,
      brandCounts,
      brandNames,
      channelCounts,
      negativeChannelCounts,
      asksNegatives,
    ],
  );

  const suggestions = useMemo((): Array<string> => {
    const out: Array<string> = [];
    const first = items[0];
    if (first) {
      out.push(
        t(`socialListening.topicPage.ask.suggestions.${first.rule}`, {
          brand: String(first.values.brand ?? ""),
          platform: platformLabel(first.values.platform),
        }),
      );
    }
    const ranked = (brandCounts?.cells ?? [])
      .filter((cell) => cell.count > 0)
      .sort(
        (a, b) =>
          measureOf(metric, b.count, b.views) - measureOf(metric, a.count, a.views) ||
          b.count - a.count ||
          a.key.localeCompare(b.key),
      );
    if (ranked.length >= 2) {
      out.push(
        t("socialListening.topicPage.ask.suggestions.compareBrands", {
          first: brandNames.get(ranked[0].key) ?? ranked[0].key,
          second: brandNames.get(ranked[1].key) ?? ranked[1].key,
        }),
      );
    }
    out.push(t("socialListening.topicPage.ask.suggestions.summarizeWeek"));
    return out.slice(0, MAX_SUGGESTIONS);
  }, [items, brandCounts, brandNames, metric, t]);

  const isAskShown = (canAsk ?? Boolean(onAsk)) && onAsk != null;

  const copyOf = (item: SocialTopicHighlightItem) => {
    const { rule, values } = item;
    const platform = platformLabel(values.platform);
    const format = (value: unknown) => formatSocialCount(Number(value ?? 0), i18n.language);
    if (rule === "top-post-reach") {
      return {
        lead: t("socialListening.topicPage.highlights.top-post-reach.lead"),
        text: t("socialListening.topicPage.highlights.top-post-reach.text", {
          author: String(values.author ?? "").trim() || platform,
          platform,
          views: format(values.views),
          percent: values.percent,
        }),
        why: t("socialListening.topicPage.highlights.top-post-reach.why"),
      };
    }
    if (rule === "loudest-brand") {
      const text = {
        reachPercent: values.reachPercent,
        postsPercent: values.postsPercent,
      };
      return {
        lead: t("socialListening.topicPage.highlights.loudest-brand.lead", {
          brand: String(values.brand ?? ""),
        }),
        text:
          metric === "reach"
            ? t("socialListening.topicPage.highlights.loudest-brand.textReach", text)
            : t("socialListening.topicPage.highlights.loudest-brand.textPosts", text),
        why: t("socialListening.topicPage.highlights.loudest-brand.why"),
      };
    }
    return {
      lead: t("socialListening.topicPage.highlights.negative-channel.lead", { platform }),
      text: t("socialListening.topicPage.highlights.negative-channel.text", {
        platform,
        count: values.count,
        total: values.total,
        percent: values.percent,
      }),
      why: t("socialListening.topicPage.highlights.negative-channel.why"),
    };
  };

  const onFilter = (item: SocialTopicHighlightItem) => {
    if (item.filter?.brandId) {
      topicFilters.setFilter("brandId", item.filter.brandId);
    }
    if (item.filter?.platform) {
      topicFilters.setFilter("platform", item.filter.platform);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const text = question.trim();
    if (text && onAsk) {
      onAsk(text);
      setQuestion("");
    }
  };

  return (
    <section
      aria-label={t("socialListening.topicPage.highlights.title")}
      className="rounded-2xl border border-card-border bg-background pb-3"
      data-testid="social-topic-highlights"
    >
      <SocialTopicPanelHeader
        title={t("socialListening.topicPage.highlights.title")}
        icon={<Star aria-hidden="true" className="size-4 text-primary" />}
      />
      {!isReady ? (
        <div className="grid gap-6 px-5 pb-1 pt-3.5 @[1180px]/topic:grid-cols-3" data-testid="social-topic-highlights-loading">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="hidden h-16 rounded-xl @[1180px]/topic:block" />
          <Skeleton className="hidden h-16 rounded-xl @[1180px]/topic:block" />
        </div>
      ) : null}
      {isReady && items.length === 0 ? (
        <p
          className="px-5 pb-1 pt-3.5 text-sm text-muted-foreground"
          data-testid="social-topic-highlights-empty"
        >
          {t("socialListening.topicPage.highlights.empty")}
        </p>
      ) : null}
      {isReady && items.length > 0 ? (
        <ol
          className="grid gap-6 px-5 pb-1 pt-3.5 @[1180px]/topic:grid-cols-3"
          data-testid="social-topic-highlights-list"
        >
          {items.map((item, index) => {
            const copy = copyOf(item);
            const cited = item.citedPostTokens
              .map((postToken) => (topPost?.token === postToken ? topPost : null))
              .filter((post): post is SocialTopicPostItem => post != null);
            return (
              <li
                key={item.rule}
                className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2.5"
                data-testid={`social-topic-highlight-${index}`}
              >
                <span className="grid size-6 place-items-center rounded-full border border-border text-xs font-semibold tabular-nums text-foreground">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p
                    className="text-sm leading-6 text-foreground"
                    data-testid={`social-topic-highlight-text-${index}`}
                  >
                    <b className="font-semibold">{copy.lead}</b> {copy.text}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {cited.map((post, citeIndex) => (
                      <button
                        key={post.token}
                        type="button"
                        data-testid={`social-topic-highlight-cite-${index}-${citeIndex}`}
                        onClick={() => onOpenPost(post)}
                        onMouseEnter={() => setActive({ kind: "post", postToken: post.token })}
                        onMouseLeave={() => setActive(null)}
                        className="inline-flex h-6 max-w-[10.5rem] items-center gap-1.5 rounded-full border border-border px-2 text-xs text-foreground hover:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span
                          className={cn("size-3 shrink-0", SOCIAL_PLATFORM_TEXT_CLASS)}
                          style={socialPlatformColorStyle(post.platform)}
                        >
                          <SocialPlatformIcon platform={post.platform} className="size-3" />
                        </span>
                        <span className="truncate">{citeLabel(post)}</span>
                      </button>
                    ))}
                    {item.filter ? (
                      <button
                        type="button"
                        data-testid={`social-topic-highlight-filter-${index}`}
                        onClick={() => onFilter(item)}
                        className="rounded-md px-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {t("socialListening.topicPage.highlights.filter")}
                      </button>
                    ) : null}
                    <span
                      tabIndex={0}
                      data-testid={`social-topic-highlight-why-${index}`}
                      aria-describedby={`social-topic-highlight-why-tip-${index}`}
                      className="group relative inline-flex items-center gap-1 rounded-md px-1 text-xs text-muted-foreground hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Info aria-hidden="true" className="size-3.5" />
                      {t("socialListening.topicPage.highlights.why")}
                      <span
                        id={`social-topic-highlight-why-tip-${index}`}
                        role="tooltip"
                        className="pointer-events-none absolute bottom-[calc(100%+0.5rem)] left-0 z-10 w-[min(15.5rem,80vw)] translate-y-1 rounded-md border border-border bg-tooltip p-2.5 text-left text-xs leading-5 text-tooltip-foreground opacity-0 shadow-lg transition-[opacity,transform] group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
                      >
                        {copy.why}
                      </span>
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
      {isAskShown ? (
        <div
          className="mx-5 mb-4 mt-3 flex flex-wrap items-center gap-2 border-t border-card-border pt-3.5"
          data-testid="social-topic-ask"
        >
          <form
            onSubmit={onSubmit}
            className="flex h-[38px] min-w-0 flex-1 basis-60 items-center gap-2 rounded-full border border-input px-3.5 text-muted-foreground focus-within:border-ring"
          >
            <MessageCircle aria-hidden="true" className="size-4 shrink-0" />
            <input
              type="text"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder={t("socialListening.topicPage.ask.placeholder")}
              aria-label={t("socialListening.topicPage.ask.placeholder")}
              data-testid="social-topic-ask-input"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </form>
          <div className="flex min-w-0 gap-1.5 overflow-x-auto" data-testid="social-topic-ask-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion}
                type="button"
                data-testid={`social-topic-ask-suggestion-${index}`}
                onClick={() => onAsk?.(suggestion)}
                className="h-[30px] shrink-0 whitespace-nowrap rounded-full border border-border px-3 text-xs text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
